import { useMemo, useState } from 'react';
import AppButton from '../atoms/AppButton';
import { formatBRL } from '../utils/currency';
import { titleCase } from '../utils/text';
import { IconImage } from '../atoms/Icon';

const priceOf = (product, variant) => Number(variant ? variant.price : product.price);

// Sem campo de ordem no backend: o preço é o proxy mais confiável para
// tamanho (P < M < G), então ordenamos por ele em vez de exibir a ordem
// crua da API.
const sellableVariants = (product) =>
  product.variants.filter((v) => v.isAvailable).sort((a, b) => a.price - b.price);

/** Variações que aceitam meia. Um produto sem variação nenhuma entra na meia
 *  pelo seu preço único.
 *
 *  Ausente conta como permitido: o padrão do backend é `true`, e um servidor
 *  sem a migration da flag mandaria `undefined` — o que desligaria o meio a
 *  meio inteiro em silêncio. Só um `false` explícito exclui. */
const halfVariants = (product) =>
  sellableVariants(product).filter((v) => v.allowsHalfAndHalf !== false);

const canBeHalf = (product) => product.variants.length === 0 || halfVariants(product).length > 0;

function Thumb({ url, size, emoji }) {
  return (
    <span className="pdv-tile-thumb" style={size ? { width: size, height: size } : undefined}>
      {url ? (
        <img src={url} alt="" />
      ) : emoji ? (
        <span className="thumb-emoji" aria-hidden="true">{emoji}</span>
      ) : (
        <IconImage size={(size || 56) * 0.4} style={{ color: '#bbb' }} />
      )}
    </span>
  );
}

function ProductTile({ product, categoryIcon, onClick, onHalf }) {
  const prices = sellableVariants(product).map((v) => Number(v.price));
  const from = prices.length ? Math.min(...prices) : Number(product.price);
  const varies = prices.length > 1 && Math.min(...prices) !== Math.max(...prices);

  return (
    <div className="pdv-tile">
      <button type="button" className="pdv-tile-main" onClick={onClick}>
        <Thumb url={product.imageUrl} emoji={categoryIcon} />
        <span className="pdv-tile-name">{product.name}</span>
        <span className="pdv-tile-price">
          {varies ? `a partir de ${formatBRL(from)}` : formatBRL(from)}
        </span>
      </button>

      {onHalf && (
        <button type="button" className="half-and-half-badge pdv-tile-half" onClick={onHalf} title="Montar meio a meio">
          ½
        </button>
      )}
    </div>
  );
}

/** Miniatura só entra se alguma opção do conjunto tiver imagem: variações e
 *  adicionais costumam não ter, e uma fileira de caixas vazias seria só ruído.
 *  Havendo pelo menos uma, todas ganham o espaço para a grade não ficar torta. */
const anyImage = (options) => options.some((o) => o.imageUrl);

function VariantGrid({ options, priceFor, onPick }) {
  const withThumbs = anyImage(options);
  return (
    <div className="pdv-grid">
      {options.map((v) => (
        <button key={v.id} type="button" className="pdv-option" onClick={() => onPick(v)}>
          {withThumbs && <Thumb url={v.imageUrl} size={44} />}
          <span className="pdv-option-name">{v.name}</span>
          <span className="pdv-tile-price">{formatBRL(priceFor(v))}</span>
        </button>
      ))}
    </div>
  );
}

function PdvProductPicker({ categories, products, onAdd }) {
  const [activeCategoryId, setActiveCategoryId] = useState(categories[0]?.id || '');
  /**
   * Fluxo do meio a meio, um passo por vez e sempre local ao produto da vez:
   *   half1 → (variação do 1º) → half2 → (variação do 2º) → adiciona
   * Nada depende de casar variação entre produtos, então não há como ficar sem
   * opção no meio do caminho.
   */
  const [step, setStep] = useState(null);
  // Adicionais são múltipla escolha, então precisam de confirmação — diferente
  // dos outros passos, que avançam no primeiro toque.
  const [chosenAddons, setChosenAddons] = useState([]);

  const productsOf = useMemo(() => {
    const map = new Map();
    for (const p of products) {
      if (!map.has(p.categoryId)) map.set(p.categoryId, []);
      map.get(p.categoryId).push(p);
    }
    return map;
  }, [products]);

  const categoryById = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);

  /** Adicionais que o item aceita: os da categoria valem para todos os
   *  produtos dela, os do produto só para ele. No meio a meio são os da
   *  primeira metade — é o que o servidor valida contra. */
  function addonsFor(product) {
    const category = categoryById.get(product.categoryId);
    const all = [...(category?.addons || []), ...(product.addons || [])];
    const seen = new Set();
    return all.filter((a) => a.isAvailable && !seen.has(a.id) && seen.add(a.id));
  }

  function finalize(base, addons) {
    const extra = addons.reduce((sum, a) => sum + Number(a.price), 0);
    onAdd({
      ...base,
      key: `${base.key}-${addons.map((a) => a.id).join('.') || 'noadd'}`,
      addonIds: addons.map((a) => a.id),
      addons: addons.map((a) => ({ id: a.id, name: a.name, price: Number(a.price) })),
      unitPrice: base.unitPrice + extra,
      quantity: 1,
    });
    setStep(null);
    setChosenAddons([]);
  }

  // Pergunta os adicionais só quando existe algum; senão o item entra direto.
  function withAddons(base, product) {
    const options = addonsFor(product);
    if (!options.length) finalize(base, []);
    else {
      setChosenAddons([]);
      setStep({ mode: 'addons', base, options });
    }
  }

  function addSingle(product, variant) {
    withAddons({
      key: `${product.id}-${variant?.id || 'base'}-${Date.now()}`,
      productId: product.id,
      variantId: variant?.id || null,
      secondProductId: null,
      secondVariantId: null,
      label: variant ? `${product.name} (${variant.name})` : product.name,
      unitPrice: priceOf(product, variant),
    }, product);
  }

  function addHalf(first, firstVariant, second, secondVariant) {
    const side = (p, v) => (v ? `${p.name} (${v.name})` : p.name);
    withAddons({
      key: `${first.id}-${second.id}-${firstVariant?.id || 'b'}-${secondVariant?.id || 'b'}-${Date.now()}`,
      productId: first.id,
      variantId: firstVariant?.id || null,
      secondProductId: second.id,
      secondVariantId: secondVariant?.id || null,
      label: `½ ${side(first, firstVariant)} + ½ ${side(second, secondVariant)}`,
      // A metade mais cara manda — mesma regra que o servidor aplica ao salvar.
      unitPrice: Math.max(priceOf(first, firstVariant), priceOf(second, secondVariant)),
    }, first);
  }

  // Depois de escolher um lado, ou pergunta a variação dele ou segue adiante.
  function pickedFirst(first, variant) {
    setStep({ mode: 'half2', first, firstVariant: variant });
  }

  function startHalf(first) {
    const options = halfVariants(first);
    if (first.variants.length) setStep({ mode: 'half1Variant', first, options });
    else pickedFirst(first, null);
  }

  function pickedSecond(second) {
    const { first, firstVariant } = step;
    const options = halfVariants(second);
    if (second.variants.length) {
      setStep({ mode: 'half2Variant', first, firstVariant, second, options });
    } else {
      addHalf(first, firstVariant, second, null);
    }
  }

  function handleTile(product) {
    const options = sellableVariants(product);
    if (options.length) setStep({ mode: 'variant', product, options });
    else addSingle(product, null);
  }

  const back = (label, onBack) => (
    <AppButton variant="ghost" onClick={onBack}>{label}</AppButton>
  );

  // ── Variação de um item comum ───────────────────────────────────────────────
  if (step?.mode === 'variant') {
    return (
      <div className="pdv-step">
        <div className="pdv-step-head">
          <div>
            <div className="pdv-step-title">{step.product.name}</div>
            <div className="pdv-step-sub">Escolha a variação</div>
          </div>
          {back('Voltar', () => setStep(null))}
        </div>
        <VariantGrid
          options={step.options}
          priceFor={(v) => Number(v.price)}
          onPick={(v) => addSingle(step.product, v)}
        />
      </div>
    );
  }

  // ── Variação da primeira metade ─────────────────────────────────────────────
  if (step?.mode === 'half1Variant') {
    return (
      <div className="pdv-step">
        <div className="pdv-step-head">
          <div>
            <div className="pdv-step-title">½ {step.first.name}</div>
            <div className="pdv-step-sub">Escolha a variação desta metade</div>
          </div>
          {back('Cancelar', () => setStep(null))}
        </div>
        {step.options.length === 0 ? (
          <p className="items-summary-empty">
            Nenhuma variação de {step.first.name} aceita meio a meio.
          </p>
        ) : (
          <VariantGrid
            options={step.options}
            priceFor={(v) => Number(v.price)}
            onPick={(v) => pickedFirst(step.first, v)}
          />
        )}
      </div>
    );
  }

  // ── Segunda metade ──────────────────────────────────────────────────────────
  if (step?.mode === 'half2') {
    const candidates = (productsOf.get(step.first.categoryId) || [])
      .filter((p) => p.id !== step.first.id && canBeHalf(p));

    return (
      <div className="pdv-step">
        <div className="pdv-step-head">
          <div>
            <div className="pdv-step-title">
              ½ {step.first.name}{step.firstVariant ? ` (${step.firstVariant.name})` : ''} + ?
            </div>
            <div className="pdv-step-sub">Escolha a segunda metade</div>
          </div>
          {back('Cancelar', () => setStep(null))}
        </div>
        {candidates.length === 0 ? (
          <p className="items-summary-empty">
            Nenhum outro produto desta categoria aceita meio a meio.
          </p>
        ) : (
          <div className="pdv-grid">
            {candidates.map((p) => (
              <ProductTile
                key={p.id}
                product={p}
                categoryIcon={categoryById.get(p.categoryId)?.icon}
                onClick={() => pickedSecond(p)}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Variação da segunda metade ──────────────────────────────────────────────
  if (step?.mode === 'half2Variant') {
    return (
      <div className="pdv-step">
        <div className="pdv-step-head">
          <div>
            <div className="pdv-step-title">
              ½ {step.first.name} + ½ {step.second.name}
            </div>
            <div className="pdv-step-sub">Escolha a variação da segunda metade</div>
          </div>
          {back('Voltar', () => setStep({
            mode: 'half2', first: step.first, firstVariant: step.firstVariant,
          }))}
        </div>
        {step.options.length === 0 ? (
          <p className="items-summary-empty">
            Nenhuma variação de {step.second.name} aceita meio a meio.
          </p>
        ) : (
          <VariantGrid
            options={step.options}
            // O preço mostrado é o do item montado: a metade mais cara.
            priceFor={(v) => Math.max(
              priceOf(step.first, step.firstVariant),
              Number(v.price),
            )}
            onPick={(v) => addHalf(step.first, step.firstVariant, step.second, v)}
          />
        )}
      </div>
    );
  }

  // ── Adicionais ──────────────────────────────────────────────────────────────
  if (step?.mode === 'addons') {
    const extra = chosenAddons.reduce((sum, a) => sum + Number(a.price), 0);
    const toggle = (addon) => setChosenAddons((prev) => (
      prev.some((a) => a.id === addon.id)
        ? prev.filter((a) => a.id !== addon.id)
        : [...prev, addon]
    ));

    return (
      <div className="pdv-step">
        <div className="pdv-step-head">
          <div>
            <div className="pdv-step-title">{step.base.label}</div>
            <div className="pdv-step-sub">Adicionais (opcional)</div>
          </div>
          {back('Cancelar', () => { setStep(null); setChosenAddons([]); })}
        </div>

        <div className="pdv-grid">
          {step.options.map((addon) => {
            const on = chosenAddons.some((a) => a.id === addon.id);
            return (
              <button
                key={addon.id}
                type="button"
                className={`pdv-option pdv-option-toggle${on ? ' on' : ''}`}
                aria-pressed={on}
                onClick={() => toggle(addon)}
              >
                {anyImage(step.options) && <Thumb url={addon.imageUrl} size={44} />}
                <span className="pdv-option-name">{addon.name}</span>
                <span className="pdv-tile-price">+ {formatBRL(Number(addon.price))}</span>
              </button>
            );
          })}
        </div>

        <div className="pdv-step-foot">
          <span style={{ fontSize: 13, color: '#777' }}>
            {chosenAddons.length === 0
              ? 'Nenhum adicional'
              : `${chosenAddons.length} adicional${chosenAddons.length > 1 ? 'is' : ''} · + ${formatBRL(extra)}`}
          </span>
          <AppButton variant="primary" onClick={() => finalize(step.base, chosenAddons)}>
            Adicionar {formatBRL(step.base.unitPrice + extra)}
          </AppButton>
        </div>
      </div>
    );
  }

  // ── Grade normal ────────────────────────────────────────────────────────────
  const activeCategory = categoryById.get(activeCategoryId);
  const visible = productsOf.get(activeCategoryId) || [];
  const halfPartners = visible.filter(canBeHalf).length;

  return (
    <div>
      <div className="pdv-tabs">
        {categories.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => setActiveCategoryId(c.id)}
            className={`pdv-tab${c.id === activeCategoryId ? ' active' : ''}`}
          >
            {c.icon && <span>{c.icon}</span>}
            {titleCase(c.name)}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <p className="items-summary-empty mt-3">Nenhum produto disponível nesta categoria.</p>
      ) : (
        <div className="pdv-grid">
          {visible.map((p) => (
            <ProductTile
              key={p.id}
              product={p}
              categoryIcon={activeCategory?.icon}
              onClick={() => handleTile(p)}
              onHalf={
                activeCategory?.allowsHalfAndHalf && canBeHalf(p) && halfPartners > 1
                  ? () => startHalf(p)
                  : null
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default PdvProductPicker;
